/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useDeferredRender } from '@/hooks/use-deferred-render';
import ProductRecommendations, { type ProductRecommendationsProps } from './index';

/**
 * Wraps `ProductRecommendations` with three-phase deferred rendering for non-critical recs carousels.
 *
 * Mounting the inner `<Suspense>` boundary on initial render forces React to retain the fallback tree and reconcile
 * the pending subtree during the critical paint window — even when the carousel is below the fold. Gating the mount on
 * `useDeferredRender` lets the above-the-fold content paint without contention, then streams the carousel in once the
 * browser is idle.
 *
 * Phase 1 (Pre-Idle): Render nothing. This wrapper is for below-the-fold carousels (above-the-fold uses
 * `<ProductRecommendations>` directly), so there is nothing on screen to reserve yet — see the render-body note.
 *
 * Phase 2 (Post-Idle, Pending): Idle callback fires; `<ProductRecommendations data={…}>` mounts its Suspense boundary.
 * The Suspense `fallback` is itself viewport-gated (hidden until the row scrolls into view), so it only appears if the
 * user has scrolled to the carousel while the loader Promise is still pending.
 *
 * Phase 3 (Resolved): The Promise resolves and the carousel renders.
 *
 * Choosing this wrapper over `<ProductRecommendations>` directly *is* the opt-in to deferred rendering. Critical,
 * above-the-fold carousels should use `<ProductRecommendations>` directly.
 *
 * Promise stability across revalidation is the caller's responsibility. Pin the loader Promise in a child whose
 * `key` represents the data's semantic identity (for example query + storefront + shopper audience), so incidental
 * revalidations retain the resolved carousel while a genuine identity change remounts the child with the current
 * Promise. A route-lifetime `useState(() => …)` pin is only safe when that route instance can never represent a
 * different identity.
 */
export default function DeferredProductRecommendations(props: ProductRecommendationsProps): ReactElement | null {
    const shouldRender = useDeferredRender(true);

    // RGBARRY-CUSTOMIZATION: render null pre-idle (was `props.fallback`) so the single viewport gate lives in the
    // inner Suspense boundary. The inner `<Suspense fallback>` is viewport-gated (starts hidden until the row is on
    // screen); rendering an *ungated* skeleton here would show it, then collapse it to null the instant the inner
    // boundary mounts with `isOnScreen=false` — a skeleton→null layout shift near the fold. Below the fold there is
    // nothing on screen to reserve during the critical paint window anyway, so null is both correct and cheaper
    // (keeps the skeleton off the LCP/TBT path). This layer decides only *when* to mount; the inner layer owns
    // *whether* to show the fallback.
    if (!shouldRender) {
        return null;
    }

    return <ProductRecommendations {...props} />;
}
